/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * Shared Monaco completion-item helpers extracted from the five per-engine
 * resolvers (INT-8 DRY). Having a single source keeps the resolver files thin
 * and prevents the kind-mapping table from drifting between engines.
 *
 * Range construction is intentionally left at each call site rather than
 * wrapped into a helper here: all five resolvers build the range from the
 * same `position.lineNumber` / `wordInfo.startColumn` / `wordInfo.endColumn`
 * inputs, but MSSQL has multiple call sites (path hints, enum values, main
 * path) that each construct the range inline from the locally-scoped
 * `wordInfo`/`position` variables. A shared helper would not reduce code
 * there, so correctness over maximal DRY applies.
 */

import type { MonacoCompletionItem } from '@/features/intellisense/ranking/rank';

/**
 * Map our engine-agnostic string kind to the Monaco numeric CompletionItemKind
 * enum value.  Returns 0 when the Monaco runtime is absent (e.g. in tests
 * that don't inject a real monaco instance).
 */
export function mapKind(
  kind: MonacoCompletionItem['kind'],
  // The Monaco `CompletionItemKind` enum object — a string→number map. Kept
  // narrow (not `any`) now that this lives in one place; `monaco`/return stay
  // `any` since they interface with the untyped Monaco global at the call site.
  CompletionItemKind: Record<string, number> | null | undefined,
): number {
  if (!CompletionItemKind) return 0;
  switch (kind) {
    case 'column':
      return CompletionItemKind.Field;
    case 'table':
      return CompletionItemKind.Class;
    case 'view':
      return CompletionItemKind.Class;
    case 'schema':
      return CompletionItemKind.Module;
    case 'database':
      return CompletionItemKind.Module;
    case 'cte':
      return CompletionItemKind.Variable;
    case 'alias':
      return CompletionItemKind.Variable;
    case 'routine':
      return CompletionItemKind.Function;
    case 'parameter':
      return CompletionItemKind.Variable;
    case 'variable':
      return CompletionItemKind.Variable;
    case 'keyword':
      return CompletionItemKind.Keyword;
    case 'snippet':
      return CompletionItemKind.Snippet;
    case 'system-function':
      return CompletionItemKind.Function;
    case 'system-object':
      return CompletionItemKind.Class;
    default:
      return CompletionItemKind.Text;
  }
}

/**
 * Translate a ranked `MonacoCompletionItem` into the runtime shape that
 * Monaco's `provideCompletionItems` expects.  Spread-omits optional fields
 * (`insertTextRules`, `command`, `tags`) rather than setting them to
 * `undefined` so Monaco doesn't see spurious properties.
 */
export function toMonacoItem(
  item: MonacoCompletionItem,
  monaco: any,
  range: { startLineNumber: number; startColumn: number; endLineNumber: number; endColumn: number },
): any {
  const CompletionItemKind = monaco?.languages?.CompletionItemKind;
  const numericKind = mapKind(item.kind, CompletionItemKind);
  const insertTextRules = item.isSnippet
    ? monaco?.languages?.CompletionItemInsertTextRule?.InsertAsSnippet
    : undefined;
  const triggerSignatureHelp = item.triggerSignatureHelp
    ? { id: 'editor.action.triggerParameterHints', title: '' }
    : undefined;
  const labelOut = item.labelDescription
    ? { label: item.label, description: item.labelDescription }
    : item.label;
  return {
    label: labelOut,
    kind: numericKind,
    // Original engine-agnostic candidate kind, preserved because `kind`
    // collapses classes (table/view→Class, schema/database→Module). Monaco
    // ignores unknown fields; the autocomplete-coverage harness reads it to
    // evaluate typed candidate-class negatives (`<any-table>` etc.).
    __candidateKind: item.kind,
    detail: item.detail ?? '',
    // IMarkdownString form — Monaco renders a plain string as plain text in
    // the suggest-details panel; { value } opts into markdown rendering.
    documentation: item.documentation ? { value: item.documentation } : '',
    insertText: item.insertText,
    ...(insertTextRules ? { insertTextRules } : {}),
    sortText: item.sortText,
    filterText: item.filterText,
    preselect: item.preselect,
    range,
    ...(triggerSignatureHelp ? { command: triggerSignatureHelp } : {}),
    ...(item.tags ? { tags: item.tags } : {}),
  };
}
